/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { css } from "@emotion/react";
import { forwardRef, Fragment, type JSX } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { useThemeValues } from "../../../../hooks/useThemeValues";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { colorMix } from "../../../../utils/colors/colorMix";
import type { CSSColor } from "../../../../utils/colors/types/CSSColor.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { Loader } from "../../../blocks/other/Loader/Loader.emotion";
import type { IconElement } from "../../icons/Icon/Icon";
import type { TextProps } from "../Text/Text.emotion";
import { Text } from "../Text/Text.emotion";

const Element: HallowElement = "button";

const dynamicStylesSizeChildrenIconValues: Record<
  NonNullable<ButtonProps["size"]>,
  { icon: string; noIcon: string }
> = {
  xs: { icon: "6px", noIcon: "8px" },
  s: { icon: "4px", noIcon: "12px" },
  m: { icon: "8px", noIcon: "16px" },
  l: { icon: "16px", noIcon: "24px" },
};

const loaderSizes = {
  xs: 12,
  s: 20,
  m: 28,
  l: 36,
};

const loadingContentStyles = (isFullWidth: boolean) =>
  css({
    display: "inline-grid",
    gridTemplateColumns: isFullWidth ? "minmax(0, 1fr)" : "auto",
    gridTemplateRows: "auto",
    placeItems: "center",
    ...(isFullWidth ? { width: "100%" } : {}),
  });

const loadingPlaceholderStyles = css({
  gridColumn: 1,
  gridRow: 1,
  visibility: "hidden",
});

const loadingOverlayStyles = css({
  gridColumn: 1,
  gridRow: 1,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// Base styles for the button
const baseStyles: Interpolation = {
  alignItems: "center",
  border: "none",
  borderRadius: "999px",
  boxSizing: "border-box",
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  transition:
    "background-color 100ms ease-out, border-color 100ms ease-out, color 100ms ease-out",
  whiteSpace: "nowrap",
};

const disabledStyles: Interpolation = {
  cursor: "not-allowed",
  opacity: 0.5,
};

const fullWidthStyles: Interpolation = {
  justifyContent: "center",
  width: "100%",
};

// Size styles
const sizeStyles: Record<ButtonProps["size"], Interpolation> = {
  xs: css({
    gap: "2px",
    height: "24px",
    padding: "0 8px",
  }),
  s: css({
    gap: "4px",
    height: "32px",
    padding: "0 16px",
  }),
  m: css({
    gap: "4px",
    height: "40px",
    padding: "8px 20px",
  }),
  l: css({
    gap: "8px",
    height: "56px",
    padding: "16px 32px",
  }),
};

// Media query for hover support
const CAN_HOVER = "@media (hover: hover)";

// Function to create variant styles
const getVariantStyles = (
  variant: string,
  color?: string,
  theme?: ThemeType,
) => {
  if (!theme) return null;

  const { semanticColors, constantSemanticColors } = theme;

  switch (variant) {
    case "primary":
      return css({
        backgroundColor: semanticColors.primary,
        color: semanticColors.secondary,

        "&:focus-visible": {
          backgroundColor: semanticColors.neutralsHigher,
          color: semanticColors.neutralsLow,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsHigher,
            color: semanticColors.secondary,
          },
        },

        "&:active": {
          backgroundColor: semanticColors.neutralsHigher,
          color: semanticColors.neutralsLow,
        },
      });

    case "secondary":
      return css({
        backgroundColor: semanticColors.secondary,
        color: semanticColors.primary,

        "&:focus-visible": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsMedium,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsLowest,
            color: semanticColors.primary,
          },
        },

        "&:active": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsMedium,
        },
      });

    case "neutral":
      return css({
        backgroundColor: semanticColors.neutralsLowest,
        color: semanticColors.primary,

        "&:focus-visible": {
          backgroundColor: semanticColors.neutralsVeryLow,
          color: semanticColors.neutralsMedHigh,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsVeryLow,
            color: semanticColors.primary,
          },
        },

        "&:active": {
          backgroundColor: semanticColors.neutralsVeryLow,
          color: semanticColors.neutralsMedHigh,
        },
      });

    case "lighten":
      return css({
        backgroundColor: constantSemanticColors.fixedLightenLow,
        color: constantSemanticColors.onColorHighest,

        "&:focus-visible": {
          backgroundColor: constantSemanticColors.fixedLightenMed,
          color: constantSemanticColors.onColorVeryHigh,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: constantSemanticColors.fixedLightenMed,
            color: constantSemanticColors.onColorHighest,
          },
        },

        "&:active": {
          backgroundColor: constantSemanticColors.fixedLightenMed,
          color: constantSemanticColors.onColorVeryHigh,
        },
      });

    case "darken":
      return css({
        backgroundColor: constantSemanticColors.onColorLow,
        color: constantSemanticColors.onColorHighest,

        "&:focus-visible": {
          backgroundColor: constantSemanticColors.onColorVeryLow,
          color: constantSemanticColors.onColorVeryHigh,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: constantSemanticColors.onColorVeryLow,
            color: constantSemanticColors.onColorHighest,
          },
        },

        "&:active": {
          backgroundColor: constantSemanticColors.onColorVeryLow,
          color: constantSemanticColors.onColorVeryHigh,
        },
      });

    case "color":
      if (!color) return null;
      return css({
        backgroundColor: color,
        color: constantSemanticColors.onColorHighest,

        "&:focus-visible": {
          backgroundColor: colorMix({
            source: color,
            adjustment: constantSemanticColors.fixedDarkenMedLow as CSSColor,
          }) as string,
          color: constantSemanticColors.onColorVeryHigh,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: colorMix({
              source: color,
              adjustment: constantSemanticColors.fixedDarkenMedLow as CSSColor,
            }) as string,
            color: constantSemanticColors.onColorHighest,
          },
        },

        "&:active": {
          backgroundColor: colorMix({
            source: color,
            adjustment: constantSemanticColors.fixedDarkenMedLow as CSSColor,
          }) as string,
          color: constantSemanticColors.onColorVeryHigh,
        },
      });

    case "white":
      return css({
        backgroundColor: constantSemanticColors.onColorHighest,
        color: theme.colors.black,

        "&:focus-visible": {
          backgroundColor: colorMix({
            source: constantSemanticColors.onColorHighest as CSSColor,
            adjustment: constantSemanticColors.fixedDarkenLow as CSSColor,
          }) as string,
          color: constantSemanticColors.onColorVeryLow,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: colorMix({
              source: constantSemanticColors.onColorHighest as CSSColor,
              adjustment: constantSemanticColors.fixedDarkenLow as CSSColor,
            }) as string,
            color: theme.colors.black,
          },
        },

        "&:active": {
          backgroundColor: colorMix({
            source: constantSemanticColors.onColorHighest as CSSColor,
            adjustment: constantSemanticColors.fixedDarkenLow as CSSColor,
          }) as string,
          color: constantSemanticColors.onColorVeryLow,
        },
      });

    case "outline":
      return css({
        backgroundColor: semanticColors.secondary,
        borderColor: semanticColors.higherContrastLowest,
        borderStyle: "solid",
        borderWidth: "2px",
        color: semanticColors.primary,

        "&:focus-visible": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsMedium,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsLowest,
            color: semanticColors.primary,
          },
        },

        "&:active": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsMedium,
        },
      });

    case "transparentNeutral":
      return css({
        backgroundColor: "transparent",
        color: semanticColors.neutralsMedium,

        "&:focus-visible": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsLow,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsLowest,
            color: semanticColors.neutralsMedium,
          },
        },

        "&:active": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsLow,
        },
      });

    case "transparentPrimary":
      return css({
        backgroundColor: "transparent",
        color: semanticColors.primary,

        "&:focus-visible": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsMedium,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsLowest,
            color: semanticColors.primary,
          },
        },

        "&:active": {
          backgroundColor: semanticColors.neutralsLowest,
          color: semanticColors.neutralsMedium,
        },
      });

    case "transparentWhite":
      return css({
        backgroundColor: "transparent",
        color: constantSemanticColors.onColorHighest,

        "&:focus-visible": {
          backgroundColor: constantSemanticColors.fixedLightenLow,
          color: constantSemanticColors.onColorVeryHigh,
        },

        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: constantSemanticColors.fixedLightenLow,
            color: constantSemanticColors.onColorHighest,
          },
        },

        "&:active": {
          backgroundColor: constantSemanticColors.fixedLightenLow,
          color: constantSemanticColors.onColorVeryHigh,
        },
      });

    case "textAccent":
      return css({
        backgroundColor: "transparent",
        color: semanticColors.accentMedium,

        "&:focus-visible": {
          color: semanticColors.accentVeryLow,
        },

        [CAN_HOVER]: {
          "&:hover": {
            color: semanticColors.accentLow,
          },
        },

        "&:active": {
          color: semanticColors.accentVeryLow,
        },
      });

    default:
      return null;
  }
};

// Function to create disabled variant styles
const getDisabledVariantStyles = (
  variant: string,
  color?: string,
  theme?: ThemeType,
) => {
  if (!theme) return null;

  const { semanticColors, constantSemanticColors, colors } = theme;

  switch (variant) {
    case "primary":
      return css({
        backgroundColor: semanticColors.primary,
        color: semanticColors.secondary,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.primary,
            color: semanticColors.secondary,
          },
        },
      });

    case "secondary":
      return css({
        backgroundColor: semanticColors.secondary,
        color: semanticColors.primary,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.secondary,
            color: semanticColors.primary,
          },
        },
      });

    case "neutral":
      return css({
        backgroundColor: semanticColors.neutralsVeryLow,
        color: semanticColors.neutralsMedium,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.neutralsVeryLow,
            color: semanticColors.neutralsMedium,
          },
        },
      });

    case "lighten":
      return css({
        backgroundColor: constantSemanticColors.onColorHigh,
        color: constantSemanticColors.onColorHighest,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: constantSemanticColors.onColorHigh,
            color: constantSemanticColors.onColorHighest,
          },
        },
      });

    case "darken":
      return css({
        backgroundColor: constantSemanticColors.onColorLow,
        color: constantSemanticColors.onColorHighest,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: constantSemanticColors.onColorLow,
            color: constantSemanticColors.onColorHighest,
          },
        },
      });

    case "color":
      if (!color) return null;
      return css({
        backgroundColor: color,
        color: constantSemanticColors.onColorHighest,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: color,
            color: constantSemanticColors.onColorHighest,
          },
        },
      });

    case "white":
      return css({
        backgroundColor: constantSemanticColors.onColorHighest,
        color: colors.black,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: constantSemanticColors.onColorHighest,
            color: colors.black,
          },
        },
      });

    case "outline":
      return css({
        backgroundColor: semanticColors.secondary,
        color: semanticColors.primary,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: semanticColors.secondary,
            color: semanticColors.primary,
          },
        },
      });

    case "transparentNeutral":
      return css({
        backgroundColor: "transparent",
        color: semanticColors.neutralsHigh,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: "transparent",
            color: semanticColors.neutralsHigh,
          },
        },
      });

    case "transparentPrimary":
      return css({
        backgroundColor: "transparent",
        color: semanticColors.primary,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: "transparent",
            color: semanticColors.primary,
          },
        },
      });

    case "transparentWhite":
      return css({
        backgroundColor: "transparent",
        color: constantSemanticColors.onColorHighest,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: "transparent",
            color: constantSemanticColors.onColorHighest,
          },
        },
      });

    case "textAccent":
      return css({
        backgroundColor: "transparent",
        color: semanticColors.accentMedium,
        [CAN_HOVER]: {
          "&:hover": {
            backgroundColor: "transparent",
            color: semanticColors.accentMedium,
          },
        },
      });

    default:
      return null;
  }
};

// Function to get dynamic padding based on icon presence
const getDynamicPadding = (
  props: Pick<ButtonProps, "endIcon" | "size" | "startIcon">,
) => {
  const { endIcon, size, startIcon } = props;
  const sizeValue = size as NonNullable<ButtonProps["size"]>;

  return css({
    paddingLeft: startIcon
      ? dynamicStylesSizeChildrenIconValues[sizeValue].icon
      : dynamicStylesSizeChildrenIconValues[sizeValue].noIcon,
    paddingRight: endIcon
      ? dynamicStylesSizeChildrenIconValues[sizeValue].icon
      : dynamicStylesSizeChildrenIconValues[sizeValue].noIcon,
  });
};

export type ButtonProps = WithAsChild<
  Omit<HallowElementProps<typeof Element>, "color"> & {
    endIcon?: IconElement;
    isFullWidth?: boolean;
    isLoading?: boolean;
    startIcon?: IconElement;
    textProps?: Omit<TextProps, "children">;
  }
> &
  (
    | {
        color?: null;
        size?: "s" | "m" | "l";
        variant?:
          | "primary"
          | "secondary"
          | "neutral"
          | "lighten"
          | "darken"
          | "white"
          | "outline"
          | "transparentPrimary"
          | "textAccent";
      }
    | {
        color?: null;
        size?: "xs" | "s" | "m" | "l";
        variant?: "transparentNeutral" | "transparentWhite";
      }
    | {
        color?: string;
        size?: "s" | "m" | "l";
        variant?: "color";
      }
  );

export const Button = forwardRef<any, ButtonProps>(
  (
    {
      asChild = false,
      className,
      children,
      color = "#9C4BE3", // Default accent color
      disabled = false,
      endIcon,
      isFullWidth = false,
      isLoading = false,
      size = "m",
      startIcon,
      textProps,
      variant = "primary",
      ...props
    },
    ref,
  ) => {
    const theme = useThemeValues();

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        endIcon && <Fragment key="endIcon">{endIcon}</Fragment>,
      ],
      beforeChildrenInject: [
        startIcon && <Fragment key="startIcon">{startIcon}</Fragment>,
      ],
      children: children as JSX.Element,
    });

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement: Element,
    });

    return (
      <Text
        ref={ref}
        asChild
        {...({ size, type: "button", ...textProps } as TextProps)}
      >
        <DeterminedElement
          className={className}
          css={[
            baseStyles,
            sizeStyles[size as keyof typeof sizeStyles],
            getVariantStyles(variant, color, theme),
            children && (endIcon || startIcon)
              ? getDynamicPadding({ endIcon, size, startIcon })
              : null,
            isFullWidth ? fullWidthStyles : null,
            disabled ? disabledStyles : null,
            disabled ? getDisabledVariantStyles(variant, color, theme) : null,
          ]}
          disabled={disabled}
          {...props}
        >
          {isLoading ? (
            <span css={loadingContentStyles(isFullWidth)}>
              <span css={loadingPlaceholderStyles}>{DeterminedChildren}</span>
              <span css={loadingOverlayStyles} aria-hidden>
                <Loader
                  color="currentColor"
                  size={loaderSizes[size as keyof typeof loaderSizes]}
                />
              </span>
            </span>
          ) : (
            DeterminedChildren
          )}
        </DeterminedElement>
      </Text>
    );
  },
);

Button.displayName = "Button";
